import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, ChevronRight, Compass, Rocket, X, ListChecks, PartyPopper } from 'lucide-react';

export function OnboardingChecklist({
  checklist = [], progress = { done: 0, total: 0 }, role,
  onAction, onStartTour, dismissed, onDismiss, onReopen,
}) {
  const total = progress.total || checklist.length || 1;
  const done = progress.done || 0;
  const pct = Math.round((done / total) * 100);
  const allDone = done >= total && total > 0;

  if (dismissed) {
    return (
      <button
        onClick={onReopen}
        data-testid="setup-guide-reopen"
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-600 hover:text-slate-900 hover:border-slate-300 shadow-sm transition-colors"
      >
        <ListChecks className="w-4 h-4 text-blue-600" />
        Setup guide · {done}/{total}
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
      data-testid="onboarding-checklist"
    >
      <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shrink-0">
              {allDone ? <PartyPopper className="w-4.5 h-4.5 text-white" /> : <Rocket className="w-4.5 h-4.5 text-white" />}
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                {allDone ? "You're all set!" : 'Get started'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {allDone
                  ? 'Nice work — your account is ready to go.'
                  : `Finish these quick steps to get the most out of ${role === 'creator' ? 'Mylikeness' : 'your workspace'}.`}
              </p>
            </div>
          </div>
          <button onClick={onDismiss} data-testid="checklist-dismiss-btn"
            className="text-slate-300 hover:text-slate-500 shrink-0" aria-label="Hide setup guide">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-slate-500">{done} of {total} complete</span>
            <span className="text-xs font-semibold text-slate-700">{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              data-testid="checklist-progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        <AnimatePresence initial={false}>
          {checklist.map((step) => (
            <div key={step.key} data-testid={`checklist-step-${step.key}`}
              className="flex items-center gap-3 px-5 sm:px-6 py-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${step.done ? 'bg-emerald-500' : 'border-2 border-slate-300'}`}>
                {step.done && <Check className="w-3 h-3 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${step.done ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                  {step.label}
                </p>
                {!step.done && <p className="text-xs text-slate-400 truncate">{step.description}</p>}
              </div>
              {!step.done && step.action_tab && (
                <Button variant="ghost" size="sm"
                  onClick={() => onAction && onAction(step.action_tab)}
                  data-testid={`checklist-action-${step.key}`}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 text-xs shrink-0">
                  Do it <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                </Button>
              )}
            </div>
          ))}
        </AnimatePresence>
      </div>

      <div className="px-5 sm:px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <button onClick={onStartTour} data-testid="checklist-tour-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900">
          <Compass className="w-3.5 h-3.5 text-blue-600" /> Take the product tour
        </button>
        {allDone && (
          <button onClick={onDismiss} className="text-xs font-medium text-blue-600 hover:text-blue-700"
            data-testid="checklist-finish-btn">
            Dismiss
          </button>
        )}
      </div>
    </motion.div>
  );
}
